<template>
  <App
    :show-app-layout="isProtectedRoute"
    :is-loading="appStore.isNavigating"
    :is-content-visible="!isProtectedRoute || customUserStorageStore.areCustomUserSettingsReady"
    :frame-color-theme="frameColorTheme"
  >
    <template #logo>
      <AppLogo />
    </template>
    <template #title>
      <AppTitle />
    </template>
    <template #actions>
      <AppActions />
    </template>
    <template #navbar>
      <AppNavbar />
    </template>
    <template
      #sidebar="{
        close, isVisible,
      }"
    >
      <AppSidebar
        :close-sidebar="close"
        :is-sidebar-visible="isVisible"
      />
    </template>
    <template #message>
      <AppLicenseCheck />
    </template>
  </App>
  <PrmToast
    :auto-z-index="false"
  />
  <PrmConfirmDialog
    data-qa="confirm-dialog"
    :pt="{
      acceptButton: {
        root: {
          'data-qa': 'accept-button',
        },
      },
    }"
  />
  <ModalMergeConflictHost />
  <ModalReleaseHighlightsHost
    :is-enabled="isProtectedRoute && customUserStorageStore.isCustomUserSettingsLoaded"
  />
</template>

<script setup lang="ts">
import PrmConfirmDialog from '@NitrexGroup/primevue/confirmdialog';
import PrmToast from '@NitrexGroup/primevue/toast';
import {
  useToast,
} from '@NitrexGroup/primevue/usetoast';
import dayjs from '@dayjs';
import {
  i18n,
} from '@i18n';
import {
  ELanguage,
} from '@qmulus/frontend-shared/type/locale.ts';
import App from '@qmulus/ui/components/app/app.vue';
import ModalMergeConflictHost from '@qmulus/ui/components/modal-merge-conflict/modal-merge-conflict-host.vue';
import {
  useConfirmLeave,
} from '@qmulus/ui/composable/use-confirm-leave/use-confirm-leave.ts';
import {
  AxiosError, isAxiosError,
} from 'axios';
import {
  storeToRefs,
} from 'pinia';
import {
  computed,
  onBeforeUnmount,
  onMounted,
  watch,
} from 'vue';
import {
  useRoute, useRouter,
} from 'vue-router';

import {
  setupQueryClient,
} from './query/client';
import {
  getAxiosErrorMessage,
} from './utils/axios';
import {
  initToast,
} from './utils/toast.ts';

import AppActions from '@/components/app/app-actions.vue';
import AppLicenseCheck from '@/components/app/app-license-check.vue';
import AppLogo from '@/components/app/app-logo.vue';
import AppNavbar from '@/components/app/app-navbar.vue';
import AppSidebar from '@/components/app/app-sidebar.vue';
import AppTitle from '@/components/app/app-title.vue';
import ModalReleaseHighlightsHost from '@/components/modal-release-highlights-host/modal-release-highlights-host.vue';
import {
  useAppearance,
} from '@/composition/use-appearance.ts';
import config from '@/config/config.ts';
import {
  ERouteName,
} from '@/router/route-name.ts';
import {
  ANONYMOUS_ROUTES, AUTH_ROUTES,
} from '@/router/routes.ts';
import {
  useAppStore,
} from '@/store/pinia/app.ts';
import {
  useAssetStore,
} from '@/store/pinia/asset.ts';
import {
  useCustomUserStorageStore,
} from '@/store/pinia/custom-user-storage.ts';
import {
  EToastLife,
} from '@/types/toast.ts';

setupQueryClient();

const toast = useToast();
initToast(toast);
const route = useRoute();
const router = useRouter();
const assetStore = useAssetStore();
assetStore.loadAssetOverview();
const appStore = useAppStore();
const {
  hasUnsavedData,
} = storeToRefs(appStore);
const customUserStorageStore = useCustomUserStorageStore();
const {
  frameColorTheme,
} = useAppearance();
customUserStorageStore.loadAndApplyCustomUserSettingsOnce();

useConfirmLeave(hasUnsavedData, router);

// TODO: do layouts and use them based on that
const isProtectedRoute = computed(() => route.name !== undefined
  && !AUTH_ROUTES.includes(route.name as ERouteName)
  && !ANONYMOUS_ROUTES.includes(route.name as ERouteName));

function globalErrorHandler(event: PromiseRejectionEvent) {
  const reason = event.reason;

  if (
    // graphql zeus error
    !reason.errors
    && !(reason instanceof AxiosError)
  ) {
    throw reason;
  }

  // graphql zeus error
  if (reason.errors) {
    toast.add({
      severity: 'error',
      summary: i18n.global.t('common.error'),
      detail: reason.errors.map((item: any) => item.message).join('; '),
      life: EToastLife.ERROR,
    });
    return;
  }

  if (isAxiosError(reason)) {
    const isPt9800Api = reason.request.responseURL.startsWith(config.VITE_APP_API_PT9800_URL);
    if (isPt9800Api) {
      // Our Authentication middleware/axios interceptor ensures that we have a valid
      // token before we issue any request. This means that any 401 can only be related
      // to missing or invalid user mappings.
      if (reason.response?.status === 401) {
        toast.add({
          severity: 'error',
          summary: i18n.global.t('common.error'),
          detail: i18n.global.t('common.errorMessage.pt9800.AxiosError401'),
          life: EToastLife.ERROR,
        });
        return;
      }
    }
  }

  toast.add({
    severity: 'error',
    summary: i18n.global.t('common.error'),
    detail: getAxiosErrorMessage(reason.response),
    life: EToastLife.ERROR,
  });
}

onMounted(() => {
  window.addEventListener('unhandledrejection', globalErrorHandler);
});

onBeforeUnmount(() => {
  window.removeEventListener('unhandledrejection', globalErrorHandler);
});

watch(
  () => appStore.locale,
  (locale) => {
    // bound to https://github.com/iamkun/dayjs/tree/dev/src/locale
    let exhaustiveCheck: never;

    switch (locale) {
      case ELanguage.DE:
      case ELanguage.EN_GB:
      case ELanguage.PL:
      case ELanguage.SK:
        dayjs.locale(locale);
        break;
      case ELanguage.EN_US:
        // dayjs does not have 'en-us' locale, it is just 'en'
        // so we do manual mapping
        dayjs.locale('en');
        break;
      default:
        exhaustiveCheck = locale;
        throw new Error(`Missing locale: ${exhaustiveCheck}.`);
    }
  },
  {
    immediate: true,
  },
);

// @ts-expect-error - assetId does not always exist, but we can handle undefined
const assetId = computed<number | undefined>(() => parseInt(route.params.assetId));
watch(
  [
    () => route.path,
    () => assetStore.isAssetOverviewLoading,
  ],
  () => {
    const routeTitleI18nKey = route.matched.at(-1)?.meta.titleI18nKey;
    const assetDisplayName = assetId.value
      ? assetStore.assetOverview(assetId.value)?.displayName
      : null;

    appStore.setAppTitle({
      routeTitle: routeTitleI18nKey ? i18n.global.t(routeTitleI18nKey) : undefined,
      subtitle: assetDisplayName,
    });
  },
  {
    immediate: true,
  },
);
</script>
